import cx from 'classnames';
import { useMedia } from 'react-use-media';
import React, { useMemo, useEffect, useState } from 'react';

import TextArea from './TextArea';
import Recorder, { RecorderProps } from './Recorder';
import VideoPreview from './VideoPreview';
import Selector, { ActiveSection } from './Selector';
import { TestimonialResponseType } from '~/types/models/testimonial';
import ProgressBar from '~/App/Components/ProgressBar';
import TextPreviewMobile from './TextPreviewMobile';
import { useTranslation } from 'react-i18next';
import { RichText } from '@vimily/bonjoro-ui-components-v2/RichTextEditor';
import styled from '@emotion/styled';
import { deleteResponse } from '~/Share/TestimonialLandingPage/Api/TestimonialRespondent';

const TextAreaContainer = styled.div`
  @media (max-width: 640px) {
    @supports (background: -webkit-named-image(i)) {
      height: 80vh !important;
    }
  }
`;
export enum QuestionResponseType {
  Recording = 'recording',
  Upload = 'upload',
  Text = 'text',
}

type QuestionDisplayProps = {
  mobile: boolean;
  bgColor: string;
} & Pick<ResponseSectionProps, 'heading' | 'question' | 'uploading'>;

function QuestionDisplay({
  heading,
  question,
  uploading,
  mobile,
  bgColor,
}: QuestionDisplayProps) {
  if (heading || question) {
    return (
      <>
        {mobile && !uploading && (
          <div
            style={{ backgroundColor: bgColor }}
            className='p-4 pb-8 flex-auto min-h-[7rem] overflow-auto v2-max-h-[50%]'
          >
            {heading}
            {question}
          </div>
        )}
      </>
    );
  }

  return null;
}

export interface ResponseSectionProps
  extends Pick<
    RecorderProps,
    | 'showBonjoroBadge'
    | 'recorderControlRef'
    | 'recorderState'
    | 'onRecorderStateChange'
  > {
  heading?: React.ReactNode;
  question?: React.ReactNode;
  acceptedResponseTypes: TestimonialResponseType[];
  minHeight?: string;
  minWidth?: string;
  disabled?: boolean;
  textResponse?: RichText;
  uploading?: boolean;
  uploadProgress?: number;
  videoSource?: string;
  updatedAt?: string;
  onTextResponseChange?: (value: RichText) => void;
  onQuestionResponseTypeSelect?: (type: QuestionResponseType) => void;
  className?: string;
  desktopView?: boolean;
  responseType?: TestimonialResponseType;
  onBackClick?: () => void;
  bgColor: string;
  testimonialUuid: string;
  manualUploadFile?: File | undefined;
  onManualUploadFileSelect?: (file: File | undefined) => void;
  responseUuid?: string;
  primaryCtaColor?: string;
  secondaryCtaColor?: string;
  ctaCornerRadius?: number;
  fontColor?: string;
  fontFamily?: string;
}

function Body({
  question,
  heading,
  acceptedResponseTypes,
  disabled,
  textResponse,
  onTextResponseChange,
  uploading,
  uploadProgress,
  videoSource,
  updatedAt,
  onQuestionResponseTypeSelect,
  showBonjoroBadge = false,
  responseType,
  onBackClick,
  bgColor,
  testimonialUuid,
  manualUploadFile,
  onManualUploadFileSelect,
  responseUuid,
  recorderControlRef,
  recorderState,
  onRecorderStateChange,
  primaryCtaColor,
  secondaryCtaColor,
  ctaCornerRadius,
  fontColor,
  fontFamily,
}: Omit<ResponseSectionProps, 'minHeight' | 'minWidth'>) {
  const fullScreenClass =
    'flex flex-col absolute h-full w-full top-0 md:relative md:pt-0 md:relative';

  const [activeSection, setActiveSection] = useState<ActiveSection>(
    ActiveSection.Selector
  );
  const [acceptsTextResponseOnly, acceptsVideoResponseOnly] = useMemo(() => {
    const acceptsTextResponseOnly =
      acceptedResponseTypes.length === 1 &&
      acceptedResponseTypes.includes(TestimonialResponseType.Text);
    const acceptsVideoResponseOnly =
      acceptedResponseTypes.length === 1 &&
      acceptedResponseTypes.includes(TestimonialResponseType.Video);

    return [acceptsTextResponseOnly, acceptsVideoResponseOnly];
  }, [acceptedResponseTypes]);

  const isMobile = useMedia({
    maxWidth: 767,
  });

  const goBackToSelector = () => {
    setActiveSection(ActiveSection.Selector);
    onBackClick?.();
  };

  const onChangeResponse = () => {
    if (responseUuid) {
      deleteResponse({ responseUuid });
    }
    goBackToSelector();
  };

  useEffect(() => {
    let activeSection = ActiveSection.Selector;
    if (responseType == TestimonialResponseType.Video && videoSource) {
      activeSection = ActiveSection.VideoPreview;
    } else if (
      acceptsTextResponseOnly ||
      responseType == TestimonialResponseType.Text
    ) {
      activeSection =
        textResponse && isMobile
          ? ActiveSection.TextPreviewMobile
          : ActiveSection.TextArea;
    }

    setActiveSection(activeSection);
  }, [acceptsTextResponseOnly, responseType, videoSource, isMobile, updatedAt]);

  useEffect(() => {
    if (activeSection === ActiveSection.Selector) {
      onManualUploadFileSelect?.(undefined);
    } else if (activeSection === ActiveSection.TextArea) {
      onQuestionResponseTypeSelect?.(QuestionResponseType.Text);
    } else if (activeSection === ActiveSection.VideoRecorder) {
      onQuestionResponseTypeSelect?.(
        manualUploadFile
          ? QuestionResponseType.Upload
          : QuestionResponseType.Recording
      );
    }
  }, [activeSection]);

  if (uploading) {
    return <UploadInProgress uploadProgress={uploadProgress as number} />;
  }

  switch (activeSection) {
    case ActiveSection.TextArea:
      return (
        <TextAreaContainer className={fullScreenClass}>
          <TextArea
            className='flex flex-col bg-white z-40 w-full h-1/2 md:h-full md:relative pt-5 overflow-hidden'
            onBackClick={goBackToSelector}
            hideBackButton={acceptsTextResponseOnly}
            value={textResponse}
            onChange={onTextResponseChange}
            showBonjoroBadge={showBonjoroBadge}
          />
          <QuestionDisplay
            question={question}
            heading={heading}
            uploading={uploading}
            mobile={isMobile}
            bgColor={bgColor}
          />
        </TextAreaContainer>
      );
    case ActiveSection.VideoRecorder:
      return (
        <div className={fullScreenClass}>
          <Recorder
            manualUpload={manualUploadFile}
            onBackClick={goBackToSelector}
            showBonjoroBadge={showBonjoroBadge}
            testimonialUuid={testimonialUuid}
            recorderControlRef={recorderControlRef}
            recorderState={recorderState}
            onRecorderStateChange={onRecorderStateChange}
          />
          <QuestionDisplay
            question={question}
            heading={heading}
            uploading={uploading}
            mobile={isMobile}
            bgColor={bgColor}
          />
        </div>
      );
    case ActiveSection.Selector:
      return (
        <Selector
          className='p-5'
          onResponseTypeClick={setActiveSection}
          onManualUpload={(file: File) => onManualUploadFileSelect?.(file)}
          hideTextResponseOption={acceptsVideoResponseOnly}
          disabled={disabled}
          primaryCtaColor={primaryCtaColor}
          secondaryCtaColor={secondaryCtaColor}
          ctaCornerRadius={ctaCornerRadius}
          fontColor={fontColor}
          fontFamily={fontFamily}
        />
      );
    case ActiveSection.VideoPreview:
      return (
        <VideoPreview
          onBackButtonClick={onChangeResponse}
          videoSource={videoSource}
          fontColor={fontColor}
          primaryCtaColor={primaryCtaColor}
          ctaCornerRadius={ctaCornerRadius}
          fontFamily={fontFamily}
        />
      );
    case ActiveSection.TextPreviewMobile:
      return (
        <TextPreviewMobile
          response={textResponse}
          hideChangeResponseButton={acceptsTextResponseOnly}
          onEditClick={() => setActiveSection(ActiveSection.TextArea)}
          onBackClick={goBackToSelector}
          primaryCtaColor={primaryCtaColor}
          secondaryCtaColor={secondaryCtaColor}
          ctaCornerRadius={ctaCornerRadius}
          fontFamily={fontFamily}
        />
      );
  }
}

interface UploadInProgressProp {
  uploadProgress: number;
}

function UploadInProgress({ uploadProgress }: UploadInProgressProp) {
  const { t } = useTranslation();
  return (
    <div className='h-full flex flex-col justify-center test-neutral-500 font-semibold'>
      <span className='text-center font-circular'>
        {t('testimonial:videoPlayer.uploading')}
      </span>
      <div className='h-[10px] mb-[20px] mx-16 p-[1px] overflow-hidden rounded bottom-0 inset-x-0 border border-solid border-neutral-200 mt-2'>
        <ProgressBar
          className='!bg-turquoise-700 relative rounded'
          now={uploadProgress}
          nested={true}
        />
      </div>
    </div>
  );
}

export default function ResponseSection({
  heading,
  question,
  acceptedResponseTypes,
  disabled = false,
  textResponse,
  uploading,
  uploadProgress,
  videoSource,
  onTextResponseChange,
  updatedAt,
  onQuestionResponseTypeSelect,
  className,
  desktopView = false,
  showBonjoroBadge = false,
  responseType,
  onBackClick,
  bgColor,
  testimonialUuid,
  manualUploadFile,
  onManualUploadFileSelect,
  responseUuid,
  recorderControlRef,
  recorderState,
  onRecorderStateChange,
  primaryCtaColor,
  secondaryCtaColor,
  ctaCornerRadius,
  fontColor,
  fontFamily,
}: ResponseSectionProps) {
  const responsiveClass =
    '!min-h-[100px] md:!min-h-unset mb-14 m-auto md:m-0 bg-transparent md:bg-white';
  const desktopOnlyClass = 'm-0 bg-white';

  return (
    <div
      id='recorder'
      className={cx(
        'flex-1 block',
        className,
        desktopView ? desktopOnlyClass : responsiveClass
      )}
    >
      <Body
        recorderControlRef={recorderControlRef}
        recorderState={recorderState}
        onRecorderStateChange={onRecorderStateChange}
        heading={heading}
        question={question}
        acceptedResponseTypes={acceptedResponseTypes}
        disabled={disabled}
        textResponse={textResponse}
        onTextResponseChange={onTextResponseChange}
        uploading={uploading}
        uploadProgress={uploadProgress}
        videoSource={videoSource}
        updatedAt={updatedAt}
        onQuestionResponseTypeSelect={onQuestionResponseTypeSelect}
        showBonjoroBadge={showBonjoroBadge}
        responseType={responseType}
        onBackClick={onBackClick}
        bgColor={bgColor}
        testimonialUuid={testimonialUuid}
        manualUploadFile={manualUploadFile}
        onManualUploadFileSelect={onManualUploadFileSelect}
        responseUuid={responseUuid}
        primaryCtaColor={primaryCtaColor}
        secondaryCtaColor={secondaryCtaColor}
        ctaCornerRadius={ctaCornerRadius}
        fontColor={fontColor}
        fontFamily={fontFamily}
      />
    </div>
  );
}
